import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MoreVertical, Edit, Trash2, Plus } from 'lucide-react';

const GLAccountManagement = ({ isViewer }) => {
  const [glAccounts, setGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGLAccount, setEditingGLAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const [formData, setFormData] = useState({
    accountName: '',
    accountNumber: '',
    isActive: true
  });

  // Dropdown states
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownGLAccount, setOpenDropdownGLAccount] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fetchGLAccounts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (includeInactive) params.append('includeInactive', 'true');
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await axios.get(`/api/gl-accounts?${params}`);
      setGLAccounts(response.data.glAccounts);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
      setError('');
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
      setError('Failed to fetch GL accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGLAccounts();
  }, [searchTerm, includeInactive, pagination.page, pagination.limit]);

  const goToPage = (page) => {
    setPagination(prev => ({ ...prev, page }));
    setSelectedAccounts([]); // Clear selections when changing pages
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId &&
          !event.target.closest('.relative') &&
          !event.target.closest('.dropdown-menu')) {
        setOpenDropdownId(null);
        setOpenDropdownGLAccount(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accountName.trim()) {
      setError('Account Name is required');
      return;
    }
    
    if (!formData.accountNumber.trim()) {
      setError('Account Number is required');
      return;
    }

    try {
      if (editingGLAccount) {
        await axios.put(`/api/gl-accounts/${editingGLAccount.id}`, formData);
        setSuccess('GL account updated successfully');
      } else {
        await axios.post('/api/gl-accounts', formData);
        setSuccess('GL account created successfully');
      }
      
      setShowModal(false);
      setEditingGLAccount(null);
      setFormData({
        accountName: '',
        accountNumber: '',
        isActive: true
      });
      fetchGLAccounts();
      setError('');
    } catch (error) {
      console.error('Error saving GL account:', error);
      setError(error.response?.data?.error || 'Failed to save GL account');
    }
  };

  const handleEdit = (glAccount) => {
    setEditingGLAccount(glAccount);
    setFormData({
      accountName: glAccount.accountName,
      accountNumber: glAccount.accountNumber,
      isActive: glAccount.isActive
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (glAccount) => {
    if (!window.confirm(`Are you sure you want to delete "${glAccount.accountName} (${glAccount.accountNumber})"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/gl-accounts/${glAccount.id}`);
      setSuccess('GL account deleted successfully');
      fetchGLAccounts();
      setError('');
    } catch (error) {
      console.error('Error deleting GL account:', error);
      setError(error.response?.data?.error || 'Failed to delete GL account');
    }
  };

  const handleNewGLAccount = () => {
    setEditingGLAccount(null);
    setFormData({
      accountName: '',
      accountNumber: '',
      isActive: true
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGLAccount(null);
    setFormData({
      accountName: '',
      accountNumber: '',
      isActive: true
    });
    setError('');
  };

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      setUploadLoading(true);
      setUploadResults(null);
      const response = await axios.post('/api/gl-accounts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResults(response.data);
      setSuccess(`Upload completed: ${response.data.summary.created} created, ${response.data.summary.duplicates} duplicates, ${response.data.summary.errors} errors`);
      fetchGLAccounts();

      // Don't close modal immediately if there are results to show
      if (response.data.summary.errors === 0) {
        setTimeout(() => setShowUploadModal(false), 2000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedAccounts.length === glAccounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(glAccounts.map(account => account.id));
    }
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedAccounts.length === 0) {
      setError('No accounts selected');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedAccounts.length} GL account(s)? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      let deletedCount = 0;
      let errorCount = 0;

      for (const accountId of selectedAccounts) {
        try {
          await axios.delete(`/api/gl-accounts/${accountId}`);
          deletedCount++;
        } catch (error) {
          errorCount++;
          console.error(`Failed to delete account ${accountId}:`, error);
        }
      }

      setSuccess(`Deleted ${deletedCount} account(s). ${errorCount > 0 ? `${errorCount} failed (may be in use).` : ''}`);
      setSelectedAccounts([]);
      fetchGLAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete accounts');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedAccounts.length === 0) {
      setError('No accounts selected');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Are you sure you want to archive ${selectedAccounts.length} GL account(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      let archivedCount = 0;

      for (const accountId of selectedAccounts) {
        try {
          await axios.put(`/api/gl-accounts/${accountId}`, { isActive: false });
          archivedCount++;
        } catch (error) {
          console.error(`Failed to archive account ${accountId}:`, error);
        }
      }

      setSuccess(`Archived ${archivedCount} account(s)`);
      setSelectedAccounts([]);
      fetchGLAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to archive accounts');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>GL Accounts</h3>
        {!isViewer && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" onClick={() => setShowUploadModal(true)}>
              Upload CSV
            </button>
            <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2" onClick={handleNewGLAccount}>
              <Plus className="h-4 w-4" />
              Add GL Account
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Filters and Bulk Actions */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap', marginBottom: selectedAccounts.length > 0 ? '15px' : '0' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search GL accounts..."
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="includeInactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <label htmlFor="includeInactive" style={{ margin: 0 }}>Include inactive</label>
          </div>
        </div>

        {/* Bulk Actions */}
        {!isViewer && selectedAccounts.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
            <span style={{ fontWeight: '500' }}>{selectedAccounts.length} selected</span>
            <button
              className="btn btn-sm btn-warning"
              onClick={handleBulkArchive}
              disabled={loading}
            >
              Archive Selected
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleBulkDelete}
              disabled={loading}
            >
              Delete Selected
            </button>
          </div>
        )}
      </div>

      {/* GL Accounts Table */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <p>Loading GL accounts...</p>
          </div>
        ) : glAccounts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No GL accounts found.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {!isViewer && (
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedAccounts.length === glAccounts.length && glAccounts.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                <th>Account Number</th>
                <th>Account Name</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {glAccounts.map((glAccount) => (
                <tr key={glAccount.id}>
                  {!isViewer && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(glAccount.id)}
                        onChange={() => handleSelectAccount(glAccount.id)}
                      />
                    </td>
                  )}
                  <td>
                    <strong>{glAccount.accountNumber}</strong>
                  </td>
                  <td>{glAccount.accountName}</td>
                  <td>
                    <span className={`badge ${glAccount.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {glAccount.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(glAccount.createdAt).toLocaleDateString('en-US')}</td>
                  <td>
                    {!isViewer && (
                      <div className="relative">
                        <button
                          className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
                          onClick={(e) => {
                            if (openDropdownId === glAccount.id) {
                              setOpenDropdownId(null);
                              setOpenDropdownGLAccount(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDropdownPosition({
                                top: rect.bottom,
                                left: rect.left - 180
                              });
                              setOpenDropdownId(glAccount.id);
                              setOpenDropdownGLAccount(glAccount);
                            }
                          }}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!loading && glAccounts.length > 0 && pagination.totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} accounts
            </div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                className="btn btn-sm"
                onClick={() => goToPage(1)}
                disabled={pagination.page === 1}
                style={{ opacity: pagination.page === 1 ? 0.5 : 1 }}
              >
                First
              </button>
              <button
                className="btn btn-sm"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{ opacity: pagination.page === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className="btn btn-sm"
                    onClick={() => goToPage(pageNum)}
                    style={{
                      backgroundColor: pagination.page === pageNum ? '#3b82f6' : 'white',
                      color: pagination.page === pageNum ? 'white' : '#374151'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                className="btn btn-sm"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                style={{ opacity: pagination.page === pagination.totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
              <button
                className="btn btn-sm"
                onClick={() => goToPage(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
                style={{ opacity: pagination.page === pagination.totalPages ? 0.5 : 1 }}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingGLAccount ? 'Edit GL Account' : 'Add New GL Account'}</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  placeholder="Enter account number"
                  required
                />
              </div>

              <div className="form-group">
                <label>Account Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                  placeholder="Enter account name"
                  required
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive" style={{ margin: 0 }}>Active</label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200">
                  {editingGLAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload GL Accounts CSV</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowUploadModal(false)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              <p>Upload a CSV file with GL account data. The CSV should have these columns:</p>
              <ul>
                <li><strong>AccountNumber</strong> - Account number</li>
                <li><strong>AccountName</strong> - Account name</li>
              </ul>
              
              {!uploadLoading && !uploadResults && (
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleUpload(file);
                    }
                  }}
                  className="form-control"
                />
              )}

              {uploadLoading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Processing CSV file...</p>
                </div>
              )}

              {uploadResults && (
                <div style={{ marginTop: '20px' }}>
                  <h4>Upload Results</h4>
                  <div className="alert alert-success">
                    <ul style={{ margin: 0 }}>
                      <li>Total processed: {uploadResults.summary.total}</li>
                      <li>Created: {uploadResults.summary.created}</li>
                      <li>Duplicates: {uploadResults.summary.duplicates}</li>
                      <li>Errors: {uploadResults.summary.errors}</li>
                    </ul>
                  </div>
                  
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <h5>Errors:</h5>
                      <div className="alert alert-error">
                        <ul style={{ margin: 0 }}>
                          {uploadResults.errors.slice(0, 10).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {uploadResults.errors.length > 10 && (
                            <li>... and {uploadResults.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '15px', textAlign: 'right' }}>
                    <button
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200"
                      onClick={() => {
                        setUploadResults(null);
                        setShowUploadModal(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fixed positioned dropdown menu */}
      {openDropdownId && openDropdownGLAccount && (
        <div
          className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 dropdown-menu"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                handleEdit(openDropdownGLAccount);
                setOpenDropdownId(null);
                setOpenDropdownGLAccount(null);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit GL Account
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownGLAccount(null);
                handleDelete(openDropdownGLAccount);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete GL Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GLAccountManagement;